import React, { useState } from 'react';
import { AxiosError } from "axios";
import { ICellRendererParams } from "ag-grid-community";

import { Constants } from "@eagerdog/constants";

import { IEventMessage } from "@eagerdog/interfaces";

import { Modal, useModal } from "src/components/Modal/Modal";
import ContextMenu/*, { IContextMenuOption }*/ from "src/components/ContextMenu/ContextMenu";
import { download } from "src/components/Download/DownloadManager";
import { toast } from "src/components/Toast/ToastManager";

import EditRegistration from "src/components/EditRegistration/EditRegistration";
import ChangeElements from "src/components/ChangeElements/ChangeElements";
import Confirm from "src/components/Confirm/Confirm";
import AssignArmband from "src/components/AssignArmband/AssignArmband";
import SendMessage, { SendMessageType } from "src/components/SendMessage/SendMessage";
import Button from "src/components/Button/Button";

import { apiService } from "../../../services";
import { useMessages } from "src/context/MessagesContext";
import type { CustomCellRendererProps } from 'ag-grid-react';

import styles from "./RegistrantsTab.module.scss";

interface CustomRegistrantCellRendererProps extends CustomCellRendererProps {
  getRegistrants(): void
}

enum ActionsRendererMenuOptions {
  Edit = "edit",
  Change = "change",
  Remove = "remove",
  ResendRegistration = "resend",
  SendRegistrationSummary = "sendRegistrationSummary",
  AssignArmband = "assignArmband",
  RegistrationSummaryOptions = "registrationSummaryOptions",
}

export const ActionsRenderer = (props: CustomRegistrantCellRendererProps) => {
  const { isShown, toggle } = useModal();
  const { initiateConversation } = useMessages();
  const [isSending, setIsSending] = useState<boolean>(false);
  const registeredUserEmail =
    props.value?.registration?.owner_account_email ||
    props.value?.registration?.owner?.email ||
    "";
  const handlerDisplayName =
    (props.value?.registration?.using_agent || props.value?.registration?.using_non_agent) &&
    props.value?.registration?.agent_name
      ? props.value.registration.agent_name
      : props.value?.registration?.owner?.display_name || "";

  // Only expose handler email for the optional CC checkbox when an explicit agent is registered
  const handlerEmail =
    (props.value?.registration?.using_agent || props.value?.registration?.using_non_agent) &&
    props.value?.registration?.agent_email
      ? props.value.registration.agent_email
      : "";

  const [selectedRowData, setSelectedRowData] = useState<ICellRendererParams | undefined>(undefined);

  const [modalContent, setModalContent] = useState<ActionsRendererMenuOptions | undefined>();
  const [removingDog, setRemovingDog] = useState<boolean>(false);
  const [includeWaitlist, setIncludeWaitlist] = useState<boolean>(true);
  const [pendingSummaryAction, setPendingSummaryAction] = useState<"download" | "send" | null>(null);

  // "Remove Dog" deletes every class the dog holds in this show (the API works per dog + show,
  // not per class). List them so a secretary who only wants to drop one element isn't surprised.
  const removedClassLabels: string[] = (props.value?.registration?.dog_classes || []).map((c: any) => {
    const label = [c.show_element, c.level, c.section, c.group_class].filter(Boolean).join(" ");
    return c.waitlist ? `${label} (waitlisted)` : label;
  });
  const showNoun = String(props.value?.registration?.show_name || "").toLowerCase().startsWith("trial") ? "trial" : "show";

  const removeDog = () => {
    setRemovingDog(true);

    apiService.removeDogFromShow(props.value.event._id, props.value.registration.show_id, props.value.registration.dog.dog_id).then((response) => {
      toast.show({
        title: "Remove Dog",
        content: "Successfully removed " + props.value.registration.dog.call_name + " from " + props.value.registration.show_type + " " + props.value.registration.show_name,
        duration: 10000,
        type: "success"
      });

      toggle();
      setModalContent(undefined);
      props.getRegistrants();
    }).catch((e: AxiosError) => {
      toast.show({
        title: "Remove Dog",
        content: "Unable to remove dog",
        duration: 10000,
        errorDetails: e,
        type: "fail"
      });
    }).finally(() => {
      setRemovingDog(false);
    });
  }

  const resendRegistration = (payload: IEventMessage) => {
    setIsSending(true)
    apiService.requestEntryForms(
      props.value.registration.event_id, props.value.registration.dog.dog_id, true, payload
    )
      .then(() => {
        const content = payload.is_test_sending
          ? `Test email sent to ${props.value.event.club.email}.`
          : `Successfully sent email to ${registeredUserEmail}`
        toast.show({
          title: "Registration Form",
          content,
          duration: 10000,
          type: "success"
        });
      })
      .catch((error) => {
        toast.show({
          title: "Registration Form",
          content: `Unable to send email to ${registeredUserEmail}`,
          duration: 10000,
          errorDetails: error,
          type: "fail"
        });
      })
      .finally(() => {
        setIsSending(false)
        toggle();
        setModalContent(undefined);
      });
  }

  const sendRegistrationSummary = async (payload: IEventMessage) => {
    setIsSending(true)
    try {
      const requests: Promise<any>[] = [
        apiService.sendRegistrationSummary(
          props.value.event._id, 
          props.value.registration.owner.owner_id,
          payload,
          includeWaitlist
        )
      ];

      if (payload.add_armbands) {
        requests.push(
          apiService.generatedArmNumbersPerShowTypeEmails(
            props.value.event._id,
            payload.is_test_sending,
            payload.is_test_sending ? props.value.event.club.email : undefined,
            props.value.registration.owner.owner_id
          )
        );
      }

      await Promise.all(requests);

      const content = payload.is_test_sending
        ? `Test email sent to ${props.value.event.club.email}.`
        : `Successfully sent registration summary to ${registeredUserEmail}`
      toast.show({
        title: "Registration Summary",
        content,
        duration: 10000,
        type: "success"
      });
    } catch (error: any) {
      toast.show({
        title: "Registration Summary",
        content: `Unable to send email to ${registeredUserEmail}`,
        duration: 10000,
        errorDetails: error,
        type: "fail"
      });
    } finally {
      setIsSending(false)
      toggle();
      setModalContent(undefined);
    }
  }

  const getModalContent = () => {
    switch(modalContent) {
      case ActionsRendererMenuOptions.Edit:
        return(<EditRegistration
          event={props.value.event}
          registration={props.value.registration}
          onComplete={() => {
            toggle();
            setModalContent(undefined);
            props.getRegistrants(); }}
            />
          );
      case ActionsRendererMenuOptions.Change:
        return (<ChangeElements
          event={props.value.event}
          registration={props.value.registration}
          onComplete={() => {
            toggle();
            setModalContent(undefined);
            props.getRegistrants();
          }}
          onClose={() => {
            toggle();
            setModalContent(undefined);
          }}
        />);
      case ActionsRendererMenuOptions.Remove:
        return(<Confirm
          message={<>
            <p>Are you sure you want to remove {props.value.registration.dog.call_name} from {props.value.registration.show_type} {props.value.registration.show_name}?</p>
            {removedClassLabels.length > 0 && <>
              <p>This removes <strong>all</strong> of {props.value.registration.dog.call_name}'s entries in this {showNoun}:</p>
              <ul>
                {removedClassLabels.map((label: string, i: number) => <li key={i}>{label}</li>)}
              </ul>
              {removedClassLabels.length > 1 && <p>To remove only one of these, use <strong>Change Elements</strong> or <strong>Edit Registration</strong> instead.</p>}
            </>}
            <p>This won't refund the transaction, you can refund on the Transactions tab.</p>
          </>}
          buttonsDisabled={removingDog}
          onConfirm={() => {
            removeDog();
          }}
          onCancel={() => {
            toggle();
            setModalContent(undefined);
          }}
        />);
      case ActionsRendererMenuOptions.ResendRegistration:
        return(<SendMessage
          isSending={isSending}
          handleSendMessage={resendRegistration}
          subject={`Your Updated Registration Form for ${props.value.event.title}`}
          messageBody={`
            <p>The registration form(s) for <strong>${props.value.registration.dog.call_name}</strong> in 
            <strong>${props.value.event.title}</strong>, hosted by <strong>${props.value.event.club_name}</strong>, has 
            been resent to you. This may be due to a modification to your registration or because you requested a copy.</p>
            <p>Please review the attached form carefully to ensure all details are correct. If you notice any 
            discrepancies or need further changes, please contact the event organizers as soon as possible.</p>
            <p>We look forward to seeing you at the event! 🐶🏆</p>
          `}
          type={SendMessageType.ResendRegistration}
          eventId={props.value.event._id}
          handlerEmail={handlerEmail}
          recipientInfo={{
            firstName: handlerDisplayName,
            lastName: '',
            email: registeredUserEmail
          }}
        />);
      case ActionsRendererMenuOptions.SendRegistrationSummary:
        return(<SendMessage
          isSending={isSending}
          handleSendMessage={sendRegistrationSummary}
          subject={`Registration Summary for ${props.value.event.title}`}
          messageBody={`
            <p>Attached is a summary of all registrations for your dogs in <strong>${props.value.event.title}</strong>, 
            hosted by <strong>${props.value.event.club_name}</strong>.</p>
            <p>This summary includes all confirmed entries and waitlist positions for each of your dogs. 
            Please review it carefully and contact us if you notice any discrepancies.</p>
            <p>We look forward to seeing you at the event! 🐶🏆</p>
          `}
          type={SendMessageType.ResendRegistration}
          eventId={props.value.event._id}
          recipientInfo={{
            firstName: handlerDisplayName,
            lastName: '',
            email: registeredUserEmail
          }}
        />);
      case ActionsRendererMenuOptions.AssignArmband:
        return(<AssignArmband
          event={props.value.event}
          registration={props.value.registration}
          rowData={selectedRowData}
          onComplete={() => {
            toggle();
            setModalContent(undefined);
            props.getRegistrants();
          }}
          onClose={() => {
            toggle();
            setModalContent(undefined);
          }}
        />);
      case ActionsRendererMenuOptions.RegistrationSummaryOptions:
        return (
          <div className={styles.summaryOptionsModal}>
            <h3 className={styles.summaryOptionsTitle}>Registration Summary Options</h3>
            <label className={styles.summaryOptionsToggle}>
              <input
                type="checkbox"
                checked={includeWaitlist}
                onChange={(e) => setIncludeWaitlist(e.target.checked)}
              />
              <span>Include waitlisted entries</span>
            </label>
            <div className={styles.summaryOptionsActions}>
              <Button
                onClick={() => {
                  toggle();
                  setModalContent(undefined);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (pendingSummaryAction === "download") {
                    download.pushDownload({
                      type: "registration_summary",
                      eventId: props.value.event._id,
                      userId: props.value.registration.owner.owner_id,
                      include_waitlist: includeWaitlist,
                    });
                    toggle();
                    setModalContent(undefined);
                  } else if (pendingSummaryAction === "send") {
                    setModalContent(ActionsRendererMenuOptions.SendRegistrationSummary);
                  }
                }}
              >
                Continue
              </Button>
            </div>
          </div>
        );
    }

    return <></>;
  }

  const getModalClassName = () => {
    switch (modalContent) {
      case ActionsRendererMenuOptions.Edit:
        return "editRegistrationModal";
      case ActionsRendererMenuOptions.Change:
        return "changeElementsModal";
      case ActionsRendererMenuOptions.Remove:
        return "removeDogModal";
      case ActionsRendererMenuOptions.ResendRegistration:
        return "sendMessageModal";
      case ActionsRendererMenuOptions.SendRegistrationSummary:
        return "sendMessageModal";
      case ActionsRendererMenuOptions.AssignArmband:
        return "assignArmbandModal";
      case ActionsRendererMenuOptions.RegistrationSummaryOptions:
        return "registrationSummaryOptionsModal";
    }

    return "";
  }

  const menuOptions = [
    {
      label: "Edit Registration",
      onClick:() => {
        toggle();
        setModalContent(ActionsRendererMenuOptions.Edit);
      }
    },
    {
      label: "Change Elements",
      onClick: () => {
        toggle();
        setModalContent(ActionsRendererMenuOptions.Change);
      }
    },
    {
      label: "Assign / Edit Armband",
      onClick: () => {
        setSelectedRowData(props.data);
        toggle();
        setModalContent(ActionsRendererMenuOptions.AssignArmband);
      }
    },
    {
      label: "Remove Dog",
      onClick: () => {
        toggle();
        setModalContent(ActionsRendererMenuOptions.Remove);
      }
    },
    {
      label: "Move to Waitlists",
      onClick: () => {
        apiService.returnShowRegistrationToWaitlist(props.value.event._id, props.value.registration._id)
          .then(() => {
            props.getRegistrants()
            toast.show({
              title: "Move to Waitlists",
              content: "You've successfully returned back the entry to the waitlists",
              duration: 10000,
              type: "success"
            });
          })
          .catch((error) => {
            toast.show({
              title: "Move to Waitlists",
              content: error.response.data.message ??  "Something went wrong, please try again later",
              duration: 10000,
              type: "fail"
            });
          })
      }
    }
  ];

  const hasEntryForms =
    props.value.event.sanctioning_club !== Constants.sanctioning_club.GENERAL ||
    props.value.registration.show_type === Constants.show_type.dock_diving;

  if (hasEntryForms) {
    menuOptions.unshift(
      {
        label: "Download Registration Form(s)",
        onClick:() => {
          download.pushDownload({
            type: "registration",
            registrantId: props.value.registration.event_id,
            dogId: props.value.registration.dog.dog_id
          });
        }
      },
      {
        label: "Download Registration Summary",
        onClick:() => {
          setIncludeWaitlist(true);
          setPendingSummaryAction("download");
          toggle();
          setModalContent(ActionsRendererMenuOptions.RegistrationSummaryOptions);
        }
      },
      {
        label: 'Re-send Registration Form(s)',
        onClick: () => {
          toggle();
          setModalContent(ActionsRendererMenuOptions.ResendRegistration);
        }
      },
      {
        label: 'Send Registration Summary',
        onClick: () => {
          setIncludeWaitlist(true);
          setPendingSummaryAction("send");
          toggle();
          setModalContent(ActionsRendererMenuOptions.RegistrationSummaryOptions);
        }
      },
    )
  }

  // Always the very first option regardless of hasEntryForms
  menuOptions.unshift({
    label: "Send Message",
    onClick: () => {
      const ownerId = props.value?.registration?.owner?.owner_id;
      const ownerName = props.value?.registration?.owner?.display_name;
      if (ownerId) initiateConversation(ownerId, ownerName, true);
    }
  });
  return (<div className={styles.ActionRenderer}>
    <ContextMenu
      options={menuOptions}
    />
    <Modal
      className={getModalClassName()}
      isShown={isShown}
      hide={() => { toggle(); setModalContent(undefined); }}
      modalContent={getModalContent()}
    />
  </div>);
};
